'use client';

import * as React from 'react';
import { clamp } from '@federa/const/numeric-clamp';
import { FULL_WIDTH_STYLE } from '@federa/const/style-size-presets';
import { buildFieldMessageId } from '@federa/utils/accessibility/aria-helpers';
import { animations } from '@federa/const/timing-tokens';
import { disabledTokens } from '@federa/utils/styles/interaction-tokens';
import { compactJoin } from '@federa/utils/strings/address-formatters';
import { FieldFooter } from './FieldFooter.tsx';
import { getFormFieldContainerStyles, getFormFieldLabelStyles } from './form-field-styles.ts';
import { formSizeStyles, getHelperTextStyles } from './form-utils.ts';
import { BARE_BOX_OVERRIDES, deriveInputMessage, deriveInputState, getLabelId, inputStateStyles } from '../primitives/input-helpers.ts';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { divider } from '../css-refs.layout.ts';
import textareaStyles from './Textarea.module.css';
import type { FormFieldState } from '@federa/types/ui-form-field';
import type { TextareaProps } from '@federa/types/ui-textarea';

function getTextareaInputStyles(opts: {
	currentSize: (typeof formSizeStyles)['md'];
	disabled: boolean;
	state: FormFieldState;
	autoResize: boolean;
	bare: boolean;
	customStyle?: React.CSSProperties;
}): React.CSSProperties {
	const { currentSize, disabled, state, autoResize, bare, customStyle } = opts;
	return {
		...FULL_WIDTH_STYLE,
		fontFamily: typography.fontFamily.sans,
		fontSize: currentSize.fontSize,
		lineHeight: currentSize.lineHeight,
		padding: currentSize.padding,
		color: colors.text.primary,
		backgroundColor: disabled ? colors.background.secondary : colors.background.primary,
		// Reads the SHARED per-state record rather than a private `hasError` ternary. The ternary had exactly two arms
		// on a three-member vocabulary, so `success` -- which `Textarea` could not even be told about before this
		// contract landed -- would have painted itself as the resting state.
		border: `${divider.thickness.thin} solid ${inputStateStyles[state].borderColor}`,
		borderRadius: borderRadius.lg,
		outline: 'none',
		resize: autoResize ? 'none' : 'vertical',
		transition: `all ${animations.duration.fast} ${animations.easing.easeInOut}`,
		cursor: disabled ? disabledTokens.cursor : 'text',
		// BEFORE `customStyle`, deliberately: `bare` is the variant's own decision and the call site's `style` must still
		// be able to override any member of it, exactly as it can override the boxed form.
		...(bare ? BARE_BOX_OVERRIDES : {}),
		...customStyle,
	};
}

// Resolve aria-labelledby: explicit prop wins, else the internal label id when a label exists.
function getTextareaAriaLabelledBy(ariaLabelledby: string | undefined, label: React.ReactNode, labelId: string): string | undefined {
	if (ariaLabelledby) return ariaLabelledby;
	if (label) return labelId;
	return undefined;
}

function useTextareaAutoResize(opts: {
	autoResize: boolean;
	minRows: number;
	maxRows: number | undefined;
	textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
	const { autoResize, minRows, maxRows, textareaRef } = opts;
	return React.useCallback(() => {
		if (autoResize && textareaRef.current) {
			const textarea = textareaRef.current;
			textarea.style.height = 'auto';
			const lineHeight = Number.parseFloat(getComputedStyle(textarea).lineHeight);
			const minHeight = minRows * lineHeight;
			const maxHeight = maxRows ? maxRows * lineHeight : Infinity;
			textarea.style.height = `${clamp(textarea.scrollHeight, minHeight, maxHeight)}px`;
		}
	}, [autoResize, minRows, maxRows, textareaRef]);
}

function useTextareaSetup(opts: {
	ref: React.ForwardedRef<HTMLTextAreaElement>;
	id: string | undefined;
	value: string | number | readonly string[] | undefined;
	autoResize: boolean;
	minRows: number;
	maxRows: number | undefined;
	onChange: React.ChangeEventHandler<HTMLTextAreaElement> | undefined;
}) {
	const { ref, id, value, autoResize, minRows, maxRows, onChange } = opts;
	const textareaRef = React.useRef<HTMLTextAreaElement>(null);
	const [charCount, setCharCount] = React.useState(0);
	const generatedId = React.useId();
	const textareaId = id ?? generatedId;
	const labelId = getLabelId(textareaId);
	React.useImperativeHandle(ref, () => textareaRef.current ?? ({} as HTMLTextAreaElement));
	const handleResize = useTextareaAutoResize({
		autoResize,
		minRows,
		maxRows,
		textareaRef,
	});
	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		setCharCount(e.target.value.length);
		onChange?.(e);
		handleResize();
	};
	// Re-fit the textarea height on controlled `value` change. DOM-side-effect only -- no setState, so it cannot trigger the cascading-render warning.
	React.useEffect(() => {
		handleResize();
	}, [value, handleResize]);
	// A controlled `value` prop is authoritative, so read its length during render instead of an effect-then-setState; the uncontrolled path uses `charCount`.
	const effectiveCharCount = value !== undefined ? String(value).length : charCount;
	return { textareaRef, charCount: effectiveCharCount, textareaId, labelId, handleChange };
}

function TextareaInput({
	textareaRef,
	textareaId,
	disabled,
	value,
	handleChange,
	maxLength,
	inputStyles,
	className,
	state,
	message,
	messageId,
	ariaLabelledby,
	label,
	labelId,
	...props
}: {
	textareaRef: React.RefObject<HTMLTextAreaElement | null>;
	textareaId: string;
	disabled: boolean;
	value: string | number | readonly string[] | undefined;
	handleChange: React.ChangeEventHandler<HTMLTextAreaElement>;
	maxLength: number | undefined;
	inputStyles: React.CSSProperties;
	className: string | undefined;
	state: FormFieldState;
	message: string | undefined;
	/** The footer message's id, built ONCE by the renderer and handed to both halves -- see the renderer's comment. */
	messageId: string;
	ariaLabelledby: string | undefined;
	label: React.ReactNode;
	labelId: string;
} & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return (
		<textarea
			ref={textareaRef}
			id={textareaId}
			disabled={disabled}
			value={value}
			onChange={handleChange}
			maxLength={maxLength}
			style={inputStyles}
			className={compactJoin([textareaStyles.textarea, className], ' ')}
			aria-invalid={state === 'error' ? 'true' : 'false'}
			aria-labelledby={getTextareaAriaLabelledBy(ariaLabelledby, label, labelId)}
			// Omitted rather than dangling when the field carries no message: the id and the footer's `id` attribute are
			// ONE VALUE built once by the renderer, not two calls of one builder that a later edit could pass different
			// arguments. `Input` reaches the same shape through `useFormFieldState`'s `helperId`.
			aria-describedby={message ? messageId : undefined}
			{...props}
		/>
	);
}

/** The public props plus the ref the `forwardRef` wrapper threads through. Named so the destructure and the renderer
 *  below take the SAME contract -- they are two halves of one render path, not two components. */
type TextareaRenderProps = TextareaProps & {
	forwardedRef: React.ForwardedRef<HTMLTextAreaElement>;
};

function destructureTextareaProps(props: TextareaRenderProps) {
	const {
		label,
		state,
		errorMessage,
		successMessage,
		helperText,
		autoResize = false,
		bare = false,
		minRows = 3,
		maxRows,
		showCount = false,
		maxLength,
		size = 'md',
		disabled = false,
		className,
		containerClassName,
		id,
		value,
		onChange,
		style,
		'aria-labelledby': ariaLabelledby,
		forwardedRef,
		...rest
	} = props;
	return {
		label,
		state,
		errorMessage,
		successMessage,
		helperText,
		autoResize,
		bare,
		minRows,
		maxRows,
		showCount,
		maxLength,
		size,
		disabled,
		className,
		containerClassName,
		id,
		value,
		onChange,
		style,
		ariaLabelledby,
		forwardedRef,
		rest,
	};
}

function useTextareaRenderSetup(p: ReturnType<typeof destructureTextareaProps>) {
	const setup = useTextareaSetup({
		ref: p.forwardedRef,
		id: p.id,
		value: p.value,
		autoResize: p.autoResize,
		minRows: p.minRows,
		maxRows: p.maxRows,
		onChange: p.onChange,
	});
	// The SAME derivation `Input`, `CurrencyInput` and `Select` run. This component used to spell its own
	// `hasError: !!error` -- a two-arm answer to a three-member vocabulary, and one that could not be told about
	// `success` at all because the prop did not exist on it.
	const state = deriveInputState(p.errorMessage, p.successMessage, p.state);
	const message = deriveInputMessage(p.errorMessage, p.successMessage, p.helperText);
	const inputStyles = getTextareaInputStyles({
		currentSize: formSizeStyles[p.size],
		disabled: p.disabled,
		state,
		autoResize: p.autoResize,
		bare: p.bare,
		customStyle: p.style,
	});
	return { setup, inputStyles, state, message };
}

function TextareaRender(props: TextareaRenderProps) {
	const p = destructureTextareaProps(props);
	const { setup, inputStyles, state, message } = useTextareaRenderSetup(p);
	// Built ONCE and handed to both halves. It was built twice from the same two arguments -- here for the footer's
	// `id` and again inside `TextareaInput` for the `aria-describedby` that must point at it -- and two calls are two
	// places a later edit can change the arguments of.
	const messageId = buildFieldMessageId(setup.textareaId, state);
	return (
		<div style={getFormFieldContainerStyles()} className={p.containerClassName}>
			{p.label && (
				<label id={setup.labelId} htmlFor={setup.textareaId} style={getFormFieldLabelStyles({ marginBottom: spacing.xs })}>
					{p.label}
				</label>
			)}
			<TextareaInput
				textareaRef={setup.textareaRef}
				textareaId={setup.textareaId}
				disabled={p.disabled}
				value={p.value}
				handleChange={setup.handleChange}
				maxLength={p.maxLength}
				inputStyles={inputStyles}
				className={p.className}
				state={state}
				message={message}
				messageId={messageId}
				ariaLabelledby={p.ariaLabelledby}
				label={p.label}
				labelId={setup.labelId}
				{...p.rest}
			/>
			<FieldFooter
				messageId={messageId}
				message={message}
				state={state}
				showCount={p.showCount}
				charCount={setup.charCount}
				maxLength={p.maxLength}
				messageStyles={getHelperTextStyles({ state })}
			/>
		</div>
	);
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>((props, ref) => (
	<TextareaRender {...props} forwardedRef={ref} />
));

Textarea.displayName = 'Textarea';
